import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

// --- SaaS JWT plumbing -------------------------------------------------------
const TOKEN_KEY = "kf_jwt";
const COOKIE_KEY = "kf_jwt";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Iter 64 — Belt-and-braces token persistence.
// Mobile Chrome (especially in Incognito or under low-memory pressure)
// will occasionally evict localStorage entries between tab switches.
// We mirror the JWT to a SameSite=Lax cookie so that even if
// localStorage is wiped, the next interceptor read still finds it.
// We deliberately do NOT use HttpOnly (we need JS read access) but
// we do mark Secure on https hosts to keep MITM exposure equivalent
// to the localStorage baseline.
function _readCookie(name) {
  if (typeof document === "undefined") return null;
  const all = String(document.cookie || "");
  for (const part of all.split(";")) {
    const [k, ...rest] = part.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("=") || "");
  }
  return null;
}

function _writeCookie(name, value) {
  if (typeof document === "undefined") return;
  const isHttps = typeof window !== "undefined" && window.location?.protocol === "https:";
  const secure = isHttps ? "; Secure" : "";
  if (value) {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
  }
}

export function setAuthToken(token) {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch (_) { /* mobile Chrome quota */ }
    _writeCookie(COOKIE_KEY, token);
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
    _writeCookie(COOKIE_KEY, "");
  }
}

export function getAuthToken() {
  // Iter 64 — read from BOTH stores. localStorage wins if both present
  // (it's the authoritative store); cookie is a transparent fallback
  // when localStorage was cleared by the browser between tab switches.
  let ls = null;
  try { ls = localStorage.getItem(TOKEN_KEY); } catch (_) { ls = null; }
  if (ls) return ls;
  const ck = _readCookie(COOKIE_KEY);
  if (ck) {
    // Self-heal: re-seed localStorage so subsequent reads are cheap.
    try { localStorage.setItem(TOKEN_KEY, ck); } catch (_) { /* ignore */ }
    return ck;
  }
  return null;
}

// Attach the JWT bearer to every request when present. If absent, the backend
// middleware falls back to the legacy MASTER_JOHN tenant so the existing
// `savvaspos/store` device-locked kiosks keep working unchanged.
//
// Iter 39 — Defensive guard: the routes below are ALWAYS sent without an
// Authorization header so that (a) login/register flows can never circular-
// loop on a stale or corrupted cached JWT, and (b) the AppLock → JWT
// exchange itself can't be blocked by an already-bad token. The whitelist
// is matched against the request URL suffix before any header is added.
const AUTH_HEADER_BYPASS = [
  "/auth/login",
  "/auth/register",
  "/auth/saas-login",
  "/auth/applock-exchange",
];

api.interceptors.request.use((config) => {
  const url = String(config.url || "");
  if (AUTH_HEADER_BYPASS.some((p) => url.endsWith(p) || url.includes(p + "?"))) {
    if (config.headers && "Authorization" in config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }
  // Iter 64 — ALWAYS overwrite the Authorization header from the live
  // store on every request. Previously we only set it when absent,
  // which let a stale Authorization survive across tab transitions
  // (e.g. an explicit `Authorization: ''` from a defaulted axios
  // instance) and produced the false "Missing bearer token" overlay.
  const t = getAuthToken();
  config.headers = config.headers || {};
  if (t) {
    config.headers.Authorization = `Bearer ${t}`;
  } else if ("Authorization" in config.headers) {
    delete config.headers.Authorization;
  }
  return config;
});

// Iter 64 — One-shot transparent retry for the race-condition where a
// tab transition fires a request *just before* the interceptor reads
// the token from storage. If the server says "Missing bearer token"
// but we DO have a token locally, we replay the original request once
// with a freshly-read Authorization header. The retried request is
// tagged so we never enter an infinite loop. This kills the false-
// positive red banner without weakening genuine auth failures.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const code = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const path = (typeof window !== "undefined" && window.location?.pathname) || "";
    const cfg = err?.config || {};

    const isMissingBearer =
      code === 401 &&
      typeof detail === "string" &&
      /missing bearer/i.test(detail);

    const tokenLooksBad =
      code === 401 &&
      typeof detail === "string" &&
      /invalid|expired/i.test(detail);

    // Race-condition transparent retry — only on Missing-bearer 401s
    // and only when we DO have a fresh token locally. One-shot guard
    // via __retried_iter64 prevents looping.
    if (isMissingBearer && !cfg.__retried_iter64) {
      const fresh = getAuthToken();
      if (fresh) {
        cfg.__retried_iter64 = true;
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${fresh}`;
        try {
          return await api.request(cfg);
        } catch (e2) {
          // Fall through to the normal error-handling below using e2,
          // not the original error.
          err = e2;
        }
      }
    }

    // Iter 38 — Only clear the JWT when the SERVER says the token itself
    // is bad ("Invalid or expired token"). A generic 401 from a legacy
    // endpoint that doesn't accept JWTs (e.g. /portal/*) used to also nuke
    // the token, which then broke the next JWT-protected call (e.g.
    // /admin/reset-sandbox) with "Missing bearer token". This match keeps
    // the JWT alive across legacy 401s. Iter 64 — also keep the token
    // alive on Missing-bearer retries that still failed: the user likely
    // hit a public endpoint that doesn't expect any token.
    if (tokenLooksBad && !path.startsWith("/register") && !path.startsWith("/saas-login")) {
      setAuthToken(null);
    }
    if (code === 402 && path !== "/billing") {
      window.location.assign("/billing");
    }
    return Promise.reject(err);
  },
);

// Iter 64 — small helper exported for components that want to suppress
// the false-positive red banner during a route-transition race. Use:
//   } catch (e) { if (!isSpuriousAuthRace(e)) toast.error(...); }
export function isSpuriousAuthRace(err) {
  const code = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (code !== 401 || typeof detail !== "string") return false;
  if (!/missing bearer/i.test(detail)) return false;
  // If we still hold a token locally, the 401 was almost certainly a
  // transient race — the response interceptor will have already retried
  // once, so we only reach here if both attempts failed against an
  // endpoint that genuinely doesn't accept this token. Even then, the
  // user has a valid session and clicking elsewhere will work, so the
  // red banner is noise.
  return !!getAuthToken();
}

/**
 * Iter 38 — AppLock → master JWT exchange.
 * Trades the device-locked savvaspos/store credentials for a freshly-
 * minted MASTER-tenant bearer token. Stores it under `kf_jwt` so every
 * subsequent api request auto-attaches Authorization: Bearer …. Returns
 * the token on success, throws on bad credentials / missing master row.
 */
export async function exchangeAppLockForJwt(username, password) {
  const { data } = await api.post("/auth/applock-exchange", {
    username,
    password,
  });
  setAuthToken(data.token);
  return data;
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const b64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${BACKEND_URL}${pathOrUrl}`;
  return `${API}/files/${pathOrUrl}`;
}
